// ═══════════════════════════════════════════════════════════════════════════
//  SCRIPTS — Pine Script v5 and Python strategy templates
// ═══════════════════════════════════════════════════════════════════════════

// ─── Pine Script Templates ────────────────────────────────────────────────────
export const PINE_SCRIPTS: Record<string, { title: string; code: string }> = {
  vega: {
    // God 2026-07-16 R&D redefinition: flagship built-in = v42 = v41 PINPOINT +
    // 200-EMA TREND + the U-TURN SAR FLIP entry, reorganised into 5 clean sections
    // (INPUTS / SESSION+INDICATORS / ENTRY / EXIT / VISUAL). Title keeps the
    // "RUDRA VEGA ... PINPOINT" marker so strategyEngine routes it to the native
    // runVegPinpointSimulation (server rudra_dna.py mirror). EMAs plotted: 5/50/200.
    title: 'MAMTA AI RUDRA VEGA (GOD DNA SNIPER) - PINPOINT + 200EMA TREND v42',
    code: `//@version=5
// ═══════════════════════════════════════════════════════════════════════════
//  MAMTA AI RUDRA VEGA (GOD DNA SNIPER) — v42  (God 2026-07-16)
//  Clean 5-section redefinition of v41 + the U-TURN SAR FLIP entry so the Pine
//  editor, the SuperChart browser sim, and the server rudra_dna.py all express
//  the SAME logic. Title keeps the "RUDRA VEGA ... PINPOINT" marker so the
//  SuperChart engine routes it to the native pinpoint sim.
//    DNA in one line: 200-EMA = trend confirm · 50-EMA = side-law + structural SL
//    · 5x50 pinpoint cross entry · U-TURN SAR flip re-entry · chop/impulse gates.
// ═══════════════════════════════════════════════════════════════════════════
strategy("MAMTA AI RUDRA VEGA (GOD DNA SNIPER) - PINPOINT + 200EMA TREND v42", overlay=true, initial_capital=100000, default_qty_type=strategy.fixed, default_qty_value=65, commission_type=strategy.commission.percent, commission_value=0.02, calc_on_every_tick=false)

// ─── SECTION 1 · INPUTS & SETTINGS ─────────────────────────────────────────
ema_fast  = input.int(5,      "Fast EMA")
ema_slow  = input.int(50,     "Slow / Side / SL EMA")
ema_trend = input.int(200,    "Trend EMA (confirmation)")
atr_len   = input.int(14,     "ATR length")
imp_atr   = input.float(0.5,  "Impulse ATR x",           step=0.1)
adx_min   = input.float(18,   "ADX chop filter",         step=0.5)
lock_x    = input.float(1.5,  "Profit-lock arm (x ATR)", step=0.1)
sl_buf    = input.float(0.5,  "SL ATR buffer (x ATR)",   step=0.1)
useTrend  = input.bool(true,  "200-EMA trend confirmation")
useUturn  = input.bool(true,  "U-turn SAR flip re-entry")
showSig   = input.bool(true,  "Show entry/exit/stop signals")
// R&D PHASE full-day window (09:15-15:30). Reverts to 0915-1015 + 1315-1526 for the 7-day run.
sWin1     = input.session("0915-1530", "Trade Window 1")
sWin2     = input.session("0915-1530", "Trade Window 2")

// ─── SECTION 2 · SESSION + INDICATORS ──────────────────────────────────────
inMorn = sWin1 != "" and not na(time(timeframe.period, sWin1, "Asia/Kolkata"))
inAft  = sWin2 != "" and not na(time(timeframe.period, sWin2, "Asia/Kolkata"))
inWin  = inMorn or inAft

e5   = ta.ema(close, ema_fast)
e50  = ta.ema(close, ema_slow)
e200 = ta.ema(close, ema_trend)
atr  = ta.atr(atr_len)
[diP, diM, adx] = ta.dmi(14, 14)

no_chop  = adx >= adx_min
sane_atr = atr > syminfo.mintick                 // block degenerate flat-fill (denormal ATR) bars
trendUp  = (not useTrend) or (close >= e200)      // 200-EMA macro trend: CE only above / PE only below
trendDn  = (not useTrend) or (close <= e200)
stackUp  = close >= e5 and close >= e50            // CE needs price above BOTH 5 & 50
stackDn  = close <= e5 and close <= e50            // PE needs price below BOTH 5 & 50

// ─── SECTION 3 · ENTRY  (pinpoint 5x50 cross  +  U-TURN SAR flip) ───────────
imp_up = (close - open) >= imp_atr * atr
imp_dn = (open - close) >= imp_atr * atr
// (a) pinpoint convergence cross
crossCe = ta.crossover(e5, e50)  and imp_up and stackUp and trendUp
crossPe = ta.crossunder(e5, e50) and imp_dn and stackDn and trendDn
// (b) U-TURN SAR flip: price crosses the 50-EMA to the opposite side of BOTH 5 & 50
//     and past the 200-EMA — re-enter opposite WITHOUT a fresh 5x50 cross.
priceDn50 = close[1] >= e50[1] and close < e50
priceUp50 = close[1] <= e50[1] and close > e50
flipPe = useUturn and priceDn50 and stackDn and trendDn      // Long band -> Short (PE)
flipCe = useUturn and priceUp50 and stackUp and trendUp      // Short band -> Long (CE)
// common gates (window + chop + real ATR) apply to every entry
gateOk     = inWin and no_chop and sane_atr
longEntry  = gateOk and (crossCe or flipCe)
shortEntry = gateOk and (crossPe or flipPe)

var float entryPx = na
var float peakFav = na
var bool  armed   = false
if longEntry
    strategy.entry("CE", strategy.long)                       // auto-reverses a short = SAR flip
    entryPx := close
    peakFav := high
    armed   := false
if shortEntry
    strategy.entry("PE", strategy.short)
    entryPx := close
    peakFav := low
    armed   := false
if strategy.position_size > 0
    peakFav := math.max(nz(peakFav, high), high)
if strategy.position_size < 0
    peakFav := math.min(nz(peakFav, low), low)

// ─── SECTION 4 · EXIT  (50-EMA structural SL · breakeven profit-floor · EOD) ─
float protStop = na
armNow = false
if strategy.position_size > 0
    slLong  = math.min(e50 - math.max(atr * sl_buf, syminfo.mintick), close - math.max(atr * sl_buf, syminfo.mintick))
    nowArm  = (not na(entryPx)) and (peakFav >= entryPx + lock_x * atr)
    armNow  := nowArm and not armed
    armed   := armed or nowArm
    protStop := armed ? math.max(slLong, entryPx + syminfo.mintick) : slLong   // ride, floored at breakeven
    strategy.exit("CEx", from_entry="CE", stop=protStop)
if strategy.position_size < 0
    slShort = math.max(e50 + math.max(atr * sl_buf, syminfo.mintick), close + math.max(atr * sl_buf, syminfo.mintick))
    nowArm  = (not na(entryPx)) and (peakFav <= entryPx - lock_x * atr)
    armNow  := nowArm and not armed
    armed   := armed or nowArm
    protStop := armed ? math.min(slShort, entryPx - syminfo.mintick) : slShort
    strategy.exit("PEx", from_entry="PE", stop=protStop)
if not inWin and strategy.position_size != 0
    strategy.close_all(comment="eod")
justClosed = strategy.position_size == 0 and strategy.position_size[1] != 0
wasArmed   = armed
if strategy.position_size == 0
    entryPx := na
    peakFav := na
    armed   := false

// ─── SECTION 5 · VISUAL SIGNALS ────────────────────────────────────────────
plotshape(showSig and longEntry  and (crossCe), "CE cross", shape.triangleup,   location.belowbar, color.new(color.green, 0), text="CE",   textcolor=color.white, size=size.small)
plotshape(showSig and shortEntry and (crossPe), "PE cross", shape.triangledown, location.abovebar, color.new(color.red, 0),   text="PE",   textcolor=color.white, size=size.small)
plotshape(showSig and longEntry  and (flipCe and not crossCe), "CE U-turn", shape.triangleup,   location.belowbar, color.new(color.lime, 0),    text="CE↺", textcolor=color.white, size=size.small)
plotshape(showSig and shortEntry and (flipPe and not crossPe), "PE U-turn", shape.triangledown, location.abovebar, color.new(color.fuchsia, 0), text="PE↺", textcolor=color.white, size=size.small)
plotshape(showSig and armNow, "Lock ARM", shape.diamond, location.belowbar, color.new(color.aqua, 0), text="lock", size=size.tiny)
plotshape(showSig and justClosed, "Exit", shape.xcross, location.abovebar, color.new(color.orange, 0), text="x", size=size.tiny)
plot(showSig ? protStop : na, "Protective stop", color=armed ? color.new(color.lime, 0) : color.new(color.orange, 0), style=plot.style_linebr, linewidth=2)
plot(e5,   "EMA 5",   color=color.new(color.orange, 0))
plot(e50,  "EMA 50",  color=color.new(color.blue, 0),   linewidth=2)
plot(e200, "EMA 200", color=color.new(color.purple, 0), linewidth=2)`,
  },
  shool: {
    title: 'MAMTA AI - Rudra Shool [Trend Follower]',
    code: `//@version=5
strategy("MAMTA AI - Rudra Shool [Trend Follower]", overlay=true, initial_capital=100000)

[supertrend, dir] = ta.supertrend(3, 10)
plot(supertrend, "ST", color = dir < 0 ? color.green : color.red, style = plot.style_linebr, linewidth=2)

[macdLine, signalLine, _] = ta.macd(close, 12, 26, 9)
macd_bull = macdLine > signalLine
k = ta.stoch(close, high, low, 14)
stoch_bull = k > 20

if dir < 0 and macd_bull and stoch_bull
    strategy.entry("Shool Long", strategy.long, alert_message="BUY")
if dir > 0
    strategy.close("Shool Long", comment="Trend Flip")
`,
  },
  sthambh: {
    title: 'MAMTA AI - Rudra Sthambh [Positional]',
    code: `//@version=5
strategy("MAMTA AI - Rudra Sthambh [Positional]", overlay=true, initial_capital=100000)

adx14 = ta.rma(ta.atr(14), 14)
ema50  = ta.ema(close, 50)
ema200 = ta.ema(close, 200)

bull_setup = ema50 > ema200 and adx14 > 25
breakout   = ta.crossover(close, ta.highest(close, 20)[1])

if bull_setup and breakout
    strategy.entry("Sthambh Long", strategy.long, alert_message="BUY", stop=close * 0.98)

plot(ema50,  "50 EMA",  color.blue,   2)
plot(ema200, "200 EMA", color.purple, 3)
`,
  },
  smc: {
    title: 'MAMTA AI - SMC Liquidity Engine',
    code: `//@version=5
strategy("MAMTA AI - SMC Liquidity Engine", overlay=true)

bull_fvg = low > high[2] and close[1] > open[1]
bear_fvg = high < low[2] and close[1] < open[1]
bull_ob  = close > open and close[1] < open[1] and volume > volume[1] * 1.5
bear_ob  = close < open and close[1] > open[1] and volume > volume[1] * 1.5

if bull_ob or bull_fvg
    strategy.entry("Smart Long",  strategy.long,  alert_message="BUY_CE")
if bear_ob or bear_fvg
    strategy.entry("Smart Short", strategy.short, alert_message="BUY_PE")

plotshape(bull_fvg, "FVG Up",   shape.arrowup,   location.belowbar, color.aqua,    size=size.tiny)
plotshape(bear_fvg, "FVG Down", shape.arrowdown, location.abovebar, color.fuchsia, size=size.tiny)
`,
  },
};

// ─── Python Strategy Templates ────────────────────────────────────────────────
export const PY_TEMPLATES: Record<string, { title: string; code: string }> = {
  ema_cross: {
    title: 'EMA Crossover',
    code: `# EMA Crossover Strategy
# Built-ins: ema(s,p)  sma(s,p)  rsi(s,p)  macd(s,f,sl,sg)
#            crossover(a,b)  crossunder(a,b)
# Toggle indicators in the ⚙ INDICATORS panel → click ⚡ Auto-Build

def signal(candles, position, ltp):
    if len(candles) < 22: return None
    closes = [c['close'] for c in candles]

    # ── EMAs ────────────────────────────────────────────────────────
    fast = ema(closes, 9)    # FAST_EMA
    slow = ema(closes, 21)   # SLOW_EMA

    # ── Extra Indicators ────────────────────────────────────────────

    # ── Signal ──────────────────────────────────────────────────────
    if crossover(fast, slow) and position != 'Long':
        return 'BUY'
    if crossunder(fast, slow) and position == 'Long':
        return 'EXIT'
    return None`,
  },
  rsi_reversal: {
    title: 'RSI Reversal (14)',
    code: `# RSI Reversal Strategy
def signal(candles, position, ltp):
    if len(candles) < 16:
        return None
    closes = [c['close'] for c in candles]
    r = rsi(closes, 14)
    if r[-1] < 30 and position != 'Long':
        return 'BUY'
    if r[-1] > 70 and position == 'Long':
        return 'EXIT'
    return None`,
  },
  vwap_bounce: {
    title: 'VWAP + EMA Scalp',
    code: `# VWAP-style scalp: price below EMA → buy; above 2R → exit
def signal(candles, position, ltp):
    if len(candles) < 21:
        return None
    closes  = [c['close'] for c in candles]
    highs   = [c['high']  for c in candles]
    lows    = [c['low']   for c in candles]
    e20     = ema(closes, 20)
    atr     = sma([highs[i] - lows[i] for i in range(len(closes))], 14)
    if position is None:
        if closes[-1] < e20[-1] and closes[-2] > e20[-2]:
            return 'BUY'
    elif position == 'Long':
        if ltp >= closes[0] + 2 * atr[-1]:
            return 'EXIT'
    return None`,
  },
};
